import { API_BASE_URL as API_BASE } from "../config/apiBaseUrl.js";

const reportedFailures = new Set();

const PLACEHOLDER_PATTERNS = [
  "placeholder",
  "data:image",
  "image-coming-soon",
  "/static/",
];

function isExternalBrokerImage(src) {
  if (!src || typeof src !== "string") {
    return false;
  }

  const value = src.trim().toLowerCase();

  if (!value.startsWith("http://") && !value.startsWith("https://")) {
    return false;
  }

  return !PLACEHOLDER_PATTERNS.some((pattern) => value.includes(pattern));
}

function isMidpointCachedImage(src) {
  if (!src || typeof src !== "string") {
    return false;
  }

  const value = src.trim().toLowerCase();

  return (
    value.includes("/cache/real-estate-images/") ||
    value.includes("real-estate-images/")
  );
}

function shouldReportFailure(context = {}) {
  if (context.imageId) {
    return true;
  }

  if (context.isCached || isMidpointCachedImage(context.imageUrl || context.src)) {
    return true;
  }

  return isExternalBrokerImage(context.imageUrl || context.src);
}

function buildDedupeKey({ imageId, imageUrl, renderContext }) {
  if (imageId) {
    return `${imageId}:${renderContext || "ANY"}`;
  }

  if (imageUrl) {
    return `${imageUrl}:${renderContext || "ANY"}`;
  }

  return null;
}

function buildPayload(context = {}) {
  const payload = {
    event_type: "IMAGE_LOAD_FAILED",
    failure_type: "IMG_ONERROR",
    render_context: context.renderContext,
    page_context: context.pageContext,
    occurred_at: new Date().toISOString(),
  };

  if (context.imageId) {
    payload.image_id = context.imageId;
  }

  if (context.canonicalPropertyId) {
    payload.canonical_property_id = context.canonicalPropertyId;
  }

  if (context.sourceKey) {
    payload.source_key = context.sourceKey;
  }

  if (context.sourceListingId) {
    payload.source_listing_id = context.sourceListingId;
  }

  if (context.isCached != null) {
    payload.is_cached = Boolean(context.isCached);
  }

  if (context.cacheStatus) {
    payload.cache_status = context.cacheStatus;
  }

  if (!context.imageId && context.imageUrl) {
    payload.image_url = context.imageUrl;
  }

  if (typeof window !== "undefined") {
    payload.viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }

  return payload;
}

function sendTelemetry(payload) {
  const url = `${API_BASE}/api/telemetry/image-failure`;
  const body = JSON.stringify(payload);

  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Never disrupt the UI for telemetry failures.
  }
}

export function reportImageLoadFailure(context = {}) {
  try {
    const src = context.imageUrl || context.src;

    if (!shouldReportFailure({ ...context, imageUrl: src })) {
      return;
    }

    const dedupeKey = buildDedupeKey({
      imageId: context.imageId,
      imageUrl: src,
      renderContext: context.renderContext,
    });

    if (!dedupeKey || reportedFailures.has(dedupeKey)) {
      return;
    }

    reportedFailures.add(dedupeKey);

    const payload = buildPayload({
      ...context,
      imageUrl: context.imageId ? undefined : src,
    });

    if (process.env.NODE_ENV === "development") {
      console.debug("[ImageTelemetry] reported failure", payload);
    }

    sendTelemetry(payload);
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("[ImageTelemetry] report failed", error);
    }
  }
}
